import { useRef, useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

// ─── Data ──────────────────────────────────────────────────────────────
const FAQS = [
  {
    id: 0,
    q: "How far in advance should I book?",
    a: "For bridal clients, I recommend booking at least 6–12 months ahead, especially for peak season (May–October). For editorial and personal sessions, 2–4 weeks is usually enough.",
  },

  {
    id: 2,
    q: "What does the bridal trial include?",
    a: "A 2-hour session where we test your full look — makeup, hair, and any accessories. We refine together until everything feels exactly right. I photograph the result so we both have a reference for the wedding day.",
  },
  {
    id: 3,
    q: "Which products do you use?",
    a: "I work with a curated kit of professional brands — a mix of long-wear, skin-friendly formulas suitable for all skin types. I'm happy to accommodate any allergies or sensitivities, just let me know in advance.",
  },
  {
    id: 5,
    q: "How do I book?",
    a: "Send me a message through my WhatsApp or Instagram with your date and a few details. We will have a quick chat and book the best date for you!",
  },
];

// ─── Styled ────────────────────────────────────────────────────────────
const Section = styled.section`
  position: relative;
  width: 100%;
  background: var(--cream);
  padding: 10rem 0 12rem;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 0;
  }
`;

const TopRule = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 7vw;
  right: 7vw;
  height: 1px;
  background: linear-gradient(
    90deg,
    var(--terracotta),
    var(--gold) 40%,
    transparent
  );
  opacity: 0.2;
  transform-origin: left;
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 860px;
  margin: 0 auto;
  padding: 0 7vw;
`;

// ── Header ─────────────────────────────────────────────────────────────
const Header = styled.div`
  margin-bottom: 5rem;
`;

const EyebrowRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.4rem;
`;
const EyebrowDash = styled(motion.span)`
  display: block;
  width: 32px;
  height: 1px;
  background: var(--terracotta);
  opacity: 0.8;
`;
const Eyebrow = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.6rem;
  font-weight: 300;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--terracotta);
`;
const HeadingWrap = styled.div`
  overflow: visible;
  margin-bottom: 0.1rem;
`;
const Heading = styled(motion.h2)`
  font-family: var(--font-serif);
  font-size: clamp(2.6rem, 4vw, 4rem);
  font-weight: 300;
  line-height: 0.95;
  color: var(--ink);
  letter-spacing: -0.01em;
`;
const HeadingItalic = styled(Heading)`
  font-style: italic;
  color: var(--terracotta);
`;

// ── Accordion list ─────────────────────────────────────────────────────
const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Item = styled(motion.div)`
  border-bottom: 1px solid rgba(122, 92, 71, 0.12);

  &:first-child {
    border-top: 1px solid rgba(122, 92, 71, 0.12);
  }
`;

const Question = styled.button`
  width: 100%;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  padding: 1.8rem 0;
  cursor: pointer;
  text-align: left;
`;

const QText = styled.span`
  font-family: var(--font-serif);
  font-size: clamp(1rem, 1.5vw, 1.25rem);
  font-weight: 300;
  color: ${({ $open }) => ($open ? "var(--terracotta)" : "var(--ink)")};
  line-height: 1.3;
  transition: color 0.3s ease;
  letter-spacing: 0.01em;
`;

const Icon = styled(motion.div)`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $open }) => ($open ? "var(--terracotta)" : "var(--warm-tan)")};
  transition: color 0.3s;
`;

const Answer = styled(motion.div)`
  overflow: hidden;
`;

const AnswerInner = styled.div`
  padding: 0 2.5rem 2rem 0;
`;

const AText = styled.p`
  font-family: var(--font-sans);
  font-size: clamp(0.8rem, 1vw, 0.9rem);
  font-weight: 200;
  letter-spacing: 0.05em;
  line-height: 1.9;
  color: var(--ink-soft);
  max-width: 60ch;
`;

// ── Bottom note ────────────────────────────────────────────────────────
const Note = styled(motion.p)`
  font-family: var(--font-serif);
  font-size: clamp(0.95rem, 1.3vw, 1.1rem);
  font-weight: 300;
  font-style: italic;
  text-align: center;
  color: var(--ink-soft);
  opacity: 0.6;
  margin-top: 4rem;
  line-height: 1.6;

  a {
    color: var(--terracotta);
    text-decoration: none;
    border-bottom: 1px solid rgba(184, 115, 85, 0.3);
    transition: border-color 0.3s;
    &:hover {
      border-color: var(--terracotta);
    }
  }
`;

// ─── Single FAQ item ───────────────────────────────────────────────────
function FaqItem({ faq, index, isInView }) {
  const [open, setOpen] = useState(false);

  return (
    <Item
      initial={{ opacity: 0, y: 16 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{
        duration: 0.7,
        delay: index * 0.08,
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      <Question onClick={() => setOpen((o) => !o)}>
        <QText $open={open}>{faq.q}</QText>
        <Icon
          $open={open}
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M7 1v12M1 7h12"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
          </svg>
        </Icon>
      </Question>

      <AnimatePresence initial={false}>
        {open && (
          <Answer
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <AnswerInner>
              <AText>{faq.a}</AText>
            </AnswerInner>
          </Answer>
        )}
      </AnimatePresence>
    </Item>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────
export default function FaqSection() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const headerRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-8%" });
  const headerInView = useInView(headerRef, { once: true, margin: "-10%" });

  // Get translated items array
  const faqs = t("faq.items", { returnObjects: true });

  const revealUp = {
    hidden: { y: "105%" },
    show: { y: "0%", transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <Section>
      <TopRule
        initial={{ scaleX: 0 }}
        animate={headerInView ? { scaleX: 1 } : {}}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
      />

      <Inner>
        <Header ref={headerRef}>
          <EyebrowRow>
            <EyebrowDash
              initial={{ scaleX: 0 }}
              animate={headerInView ? { scaleX: 1 } : {}}
              transition={{ delay: 0.2, duration: 0.6 }}
              style={{ transformOrigin: "left" }}
            />
            <Eyebrow
              initial={{ opacity: 0, x: -8 }}
              animate={headerInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              {t("faq.eyebrow")}
            </Eyebrow>
          </EyebrowRow>

          <HeadingWrap>
            <Heading
              variants={revealUp}
              initial="hidden"
              animate={headerInView ? "show" : "hidden"}
              transition={{ delay: 0.25 }}
            >
              {t("faq.heading_line1")}
            </Heading>
          </HeadingWrap>
          <HeadingWrap>
            <HeadingItalic
              as={motion.h2}
              variants={revealUp}
              initial="hidden"
              animate={headerInView ? "show" : "hidden"}
              transition={{ delay: 0.37 }}
            >
              {t("faq.heading_line2")}
            </HeadingItalic>
          </HeadingWrap>
        </Header>

        <List ref={ref}>
          {Array.isArray(faqs) &&
            faqs.map((faq, i) => (
              <FaqItem key={i} faq={faq} index={i} isInView={isInView} />
            ))}
        </List>

        <Note
          initial={{ opacity: 0, y: 12 }}
          animate={isInView ? { opacity: 0.6, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          <Trans i18nKey="faq.footer_note">
            Still have a question? <Link to="/contact">Send me a message</Link>
          </Trans>
          <br />
          {t("faq.footer_sub")}
        </Note>
      </Inner>
    </Section>
  );
}
